import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import Navigation from "@/components/Navigation";
import PageMeta from "@/components/seo/PageMeta";
import JsonLd, { organizationSchema, websiteSchema } from "@/components/seo/JsonLd";
import Hero from "@/components/sections/Hero";
import FeatureCards from "@/components/sections/FeatureCards";
import About from "@/components/sections/About";
import Projects from "@/components/sections/Projects";
import SubmissionHub from "@/components/sections/SubmissionHub";
import SiteFooter from "@/components/SiteFooter";

import { formHref, FORM_ANCHORS } from "@/constants/formAnchors";

/** Legacy home hashes that used to host full forms → dedicated routes (with form-top anchors). */
const HASH_FORM_REDIRECTS = {
  casting: formHref("talent"),
  partner: formHref("partner"),
  contact: formHref("contact"),
  "project-inquiry": formHref("project"),
  "talent-submission-form": formHref("talent"),
  "partner-form": formHref("partner"),
  "general-inquiry-form": formHref("contact"),
  "project-inquiry-form": formHref("project"),
  "production-network-form": formHref("crew"),
  [FORM_ANCHORS.crew.hash]: formHref("crew"),
  "choose-your-form": "/submit",
};

export default function SoKnoLanding() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const hash = location.hash;
    if (!hash) return undefined;
    const id = hash.replace(/^#/, "");
    const dest = HASH_FORM_REDIRECTS[id];
    if (dest) {
      navigate(dest, { replace: true });
      return undefined;
    }
    const scroll = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    const t = window.setTimeout(scroll, 60);
    return () => window.clearTimeout(t);
  }, [location.hash, location.pathname, navigate]);

  return (
    <div className="App" data-testid="sokno-landing">
      <PageMeta
        title="SoKno Productions | Bold Independent Film Studio"
        description="SoKno Productions is a Dallas-based independent film company developing original films, creative partnerships, and opportunities for talent, filmmakers, and production collaborators."
        path="/"
      />
      <JsonLd id="sokno-jsonld-org" data={organizationSchema()} />
      <JsonLd id="sokno-jsonld-website" data={websiteSchema()} />
      <Navigation />
      <main id="main-content">
        <Hero />
        <FeatureCards />
        <About />
        <Projects />
        <SubmissionHub />
      </main>
      <SiteFooter />

      <Toaster
        position="top-center"
        theme="dark"
        richColors
        toastOptions={{
          style: {
            background: "var(--sokno-ink-3)",
            border: "1px solid var(--sokno-line-strong)",
            color: "var(--sokno-cream)",
            fontFamily: "Manrope, sans-serif",
          },
        }}
      />
    </div>
  );
}
